import React, { Component } from "react";
import { Navigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { toast } from 'react-toastify';

import AuthService from "../services/auth.service";
import { withRouter } from "../common/with-router";

class ResetPassword extends Component {
  constructor(props) {
    super(props);

    this.state = {
      password: "",
      confirmPassword: "",
      token: "",
      loading: false,
      message: "",
      successful: false,
      validationErrors: {},
      tokenValid: null // null = checking, true = valid, false = invalid
    };

    this.onChangePassword = this.onChangePassword.bind(this);
    this.onChangeConfirmPassword = this.onChangeConfirmPassword.bind(this);
    this.handleResetPassword = this.handleResetPassword.bind(this);
  }

  componentDidMount() {
    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (!token) {
      this.setState({ 
        tokenValid: false, 
        message: "Invalid or missing reset token. Please request a new password reset." 
      });
      return;
    }

    this.setState({ token, tokenValid: true });
  }

  onChangePassword(e) {
    this.setState({
      password: e.target.value,
      validationErrors: { ...this.state.validationErrors, password: null }
    });
  }

  onChangeConfirmPassword(e) {
    this.setState({
      confirmPassword: e.target.value,
      validationErrors: { ...this.state.validationErrors, confirmPassword: null }
    });
  }

  validateForm() {
    const errors = {};
    const { password, confirmPassword } = this.state;

    // Password validation
    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters long";
    }

    // Confirm password validation
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    this.setState({ validationErrors: errors });
    return Object.keys(errors).length === 0;
  }

  handleResetPassword(e) {
    e.preventDefault();

    if (!this.validateForm()) {
      return;
    }

    this.setState({
      message: "",
      loading: true
    });

    AuthService.resetPassword(this.state.token, this.state.password)
      .then(response => {
        this.setState({
          message: response.data.message || "Password reset successfully! You can now log in with your new password.",
          successful: true,
          loading: false
        });
        
        toast.success("Password reset successfully! Redirecting to login...");
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          this.props.router.navigate("/login");
        }, 3000);
      })
      .catch(error => {
        const resMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          "An error occurred while resetting your password. Please try again.";

        this.setState({
          message: resMessage,
          successful: false,
          loading: false
        });

        toast.error(resMessage);
      });
  }

  render() {
    const { 
      password, 
      confirmPassword, 
      loading, 
      message, 
      successful, 
      validationErrors,
      tokenValid 
    } = this.state;

    // Redirect if user is already logged in
    if (AuthService.getCurrentUser()) {
      return <Navigate to="/home" />;
    }

    // Show error if token is invalid
    if (tokenValid === false) {
      return (
        <Container>
          <Row className="justify-content-center">
            <Col md={6} lg={4}>
              <Card className="mt-5">
                <Card.Header className="text-center">
                  <h4>Password Reset</h4>
                </Card.Header>
                <Card.Body>
                  <Alert variant="danger">
                    {message}
                  </Alert>
                  <div className="text-center">
                    <Button 
                      variant="primary" 
                      onClick={() => this.props.router.navigate("/login")}
                    >
                      Go to Login
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      );
    }

    // Show loading if still checking token
    if (tokenValid === null) {
      return (
        <Container>
          <Row className="justify-content-center">
            <Col md={6} lg={4}>
              <Card className="mt-5">
                <Card.Body className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Validating reset token...</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      );
    }

    return (
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            <Card className="mt-5">
              <Card.Header className="text-center">
                <h4>Reset Your Password</h4>
              </Card.Header>
              <Card.Body>
                {message && (
                  <Alert variant={successful ? "success" : "danger"}>
                    {message}
                  </Alert>
                )}

                {!successful && (
                  <Form onSubmit={this.handleResetPassword}>
                    <Form.Group className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        value={password}
                        onChange={this.onChangePassword}
                        isInvalid={!!validationErrors.password}
                        placeholder="Enter your new password"
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.password}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        value={confirmPassword}
                        onChange={this.onChangeConfirmPassword}
                        isInvalid={!!validationErrors.confirmPassword}
                        placeholder="Confirm your new password"
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.confirmPassword}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <div className="d-grid">
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Resetting Password...
                          </>
                        ) : (
                          "Reset Password"
                        )}
                      </Button>
                    </div>
                  </Form>
                )}

                <div className="text-center mt-3">
                  <Button 
                    variant="link" 
                    onClick={() => this.props.router.navigate("/login")}
                    disabled={loading}
                  >
                    Back to Login
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default withRouter(ResetPassword);
